import React, { useState, useEffect, useCallback, useRef } from 'react';
import { agentFetch } from '../../utils/agentFetch';

// --- Feature flags (kill switches) — see RDF-Agent/CHANGE_MANIFEST_query-hover.md ---
// Flip either to false + rebuild + `firebase deploy --only hosting` to remove that feature.
const ONLY_SHOW_USED_FIRM_PILLS = true; // change 1: only show firm pills that have usage in range
const ENABLE_QUERY_HOVER        = true; // change 2: hover a usage row to see the query asked
const ENABLE_QUERY_DETAIL       = true; // change 3: click a usage row for full question/answer + copy

type AdminTab = 'orgs' | 'usage' | 'sync';

interface Org {
  id: string;
  name: string;
  spend_cap_gbp: number;
  total_spend_gbp: number;
  is_active: boolean;
}

interface SyncStatus {
  status: 'idle' | 'running' | 'done';
  doc_num?: number;
  doc_total?: number;
  doc_name?: string;
  docs_added?: string[];
  docs_removed?: string[];
  docs_updated?: string[];
  error?: string;
}

interface Props {
  isSuperAdmin: boolean;
}

// ----- Pending Approvals Tab -----
// ----- Organisations Tab -----
function OrgsTab() {
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingCap, setEditingCap] = useState<Record<string, string>>({});
  const [mergingFrom, setMergingFrom] = useState<string | null>(null);
  const [mergeTarget, setMergeTarget] = useState<string>('');
  const [merging, setMerging] = useState(false);
  const [mergeConfirming, setMergeConfirming] = useState(false);
  const [mergeError, setMergeError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await agentFetch('/admin/api/orgs');
      if (r.ok) setOrgs(await r.json());
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveCap = async (orgId: string) => {
    const val = parseFloat(editingCap[orgId] ?? '');
    if (isNaN(val)) return;
    await agentFetch(`/admin/api/orgs/${orgId}/cap`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ spend_cap_gbp: val }),
    });
    setEditingCap(p => { const n = { ...p }; delete n[orgId]; return n; });
    load();
  };

  const doMerge = async () => {
    if (!mergingFrom || !mergeTarget || mergingFrom === mergeTarget) return;
    setMerging(true);
    setMergeError(null);
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 30000);
      let r: Response;
      try {
        r = await agentFetch(`/admin/api/orgs/${mergingFrom}/merge-into/${mergeTarget}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timer);
      }
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        setMergeError(`Merge failed: ${body.error || r.statusText}`);
      } else {
        setMergingFrom(null);
        setMergeTarget('');
        setMergeError(null);
        setMergeConfirming(false);
        load();
      }
    } catch (e: any) {
      setMergeError(e.name === 'AbortError' ? 'Request timed out after 30s — container may be cold, please try again.' : `Merge error: ${e.message}`);
    }
    setMerging(false);
  };

  const colCount = 6;

  if (loading) return <p className="text-muted text-sm">Loading…</p>;

  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead><tr><th>Org</th><th>Spend vs Cap</th><th style={{width:75}}>Cap (£)</th><th style={{width:70}}>Status</th><th style={{width:60}}></th><th style={{fontSize:11,color:'var(--c-text-muted)',width:90}}>ID</th></tr></thead>
        <tbody>
          {orgs.map(o => {
            const pct = o.spend_cap_gbp > 0 ? Math.min(100, (o.total_spend_gbp / o.spend_cap_gbp) * 100) : 0;
            const barColor = pct >= 90 ? '#dc2626' : pct >= 70 ? '#d97706' : '#16a34a';
            const isExpanded = mergingFrom === o.id;
            return (
              <React.Fragment key={o.id}>
                <tr>
                  <td>{o.name}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 60, height: 6, background: 'var(--c-border)', borderRadius: 4, overflow: 'hidden', flexShrink: 0 }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: 4, transition: 'width 0.3s' }} />
                      </div>
                      <span style={{ fontSize: 11, color: 'var(--c-text-muted)', whiteSpace: 'nowrap' }}>
                        £{o.total_spend_gbp.toFixed(2)} / £{o.spend_cap_gbp.toFixed(2)}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        type="number"
                        step="0.01"
                        className="form-input"
                        style={{ width: 55, padding: '2px 4px', fontSize: 12 }}
                        value={editingCap[o.id] ?? o.spend_cap_gbp}
                        onChange={e => setEditingCap(p => ({ ...p, [o.id]: e.target.value }))}
                      />
                      {editingCap[o.id] !== undefined && (
                        <button className="btn btn-sm btn-primary" onClick={() => saveCap(o.id)}>Save</button>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${o.is_active ? 'badge-accent' : 'badge-neutral'}`} style={{fontSize:10,padding:'2px 6px'}}>
                      {o.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline"
                      style={{ fontSize: 11 }}
                      onClick={() => { setMergingFrom(isExpanded ? null : o.id); setMergeTarget(''); setMergeConfirming(false); setMergeError(null); }}
                    >{isExpanded ? 'Cancel' : 'Link→'}</button>
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--c-text-muted)', whiteSpace: 'nowrap' }}>{o.id}</td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={colCount} style={{ background: 'var(--c-bg-subtle)', padding: '10px 14px' }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13 }}>Link into:</span>
                        <select
                          className="form-input"
                          style={{ width: 220, padding: '4px 8px', fontSize: 13 }}
                          value={mergeTarget}
                          onChange={e => { setMergeTarget(e.target.value); setMergeConfirming(false); setMergeError(null); }}
                        >
                          <option value="">— select target —</option>
                          {orgs.filter(x => x.id !== o.id).map(x => (
                            <option key={x.id} value={x.id}>{x.name} ({x.id})</option>
                          ))}
                        </select>
                        {!mergeConfirming ? (
                          <button className="btn btn-sm btn-outline" disabled={!mergeTarget} onClick={() => setMergeConfirming(true)}>Link→</button>
                        ) : (
                          <>
                            <span style={{ fontSize: 12, color: 'var(--c-error)' }}>Cannot be undone.</span>
                            <button className="btn btn-sm btn-primary" disabled={merging} onClick={doMerge}>
                              {merging ? 'Merging…' : 'Confirm'}
                            </button>
                            <button className="btn btn-sm btn-outline" onClick={() => setMergeConfirming(false)}>Back</button>
                          </>
                        )}
                      </div>
                      {mergeError && <p style={{ color: 'var(--c-error)', fontSize: 12, marginTop: 8 }}>⚠ {mergeError}</p>}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ----- Usage Tab -----
function UsageTab({ isSuperAdmin }: { isSuperAdmin: boolean }) {
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(thirtyDaysAgo);
  const [dateTo, setDateTo] = useState(tomorrow);
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [minCost, setMinCost] = useState('');
  const [hoverQuery, setHoverQuery] = useState<{ query: string; answer: string; x: number; y: number } | null>(null);
  const [detail, setDetail] = useState<any | null>(null);
  const [copied, setCopied] = useState<'q' | 'a' | null>(null);

  useEffect(() => {
    if (!detail) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDetail(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detail]);

  const copyText = async (text: string, which: 'q' | 'a') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(c => (c === which ? null : c)), 1500);
    } catch {
      setCopied(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await agentFetch(`/admin/api/usage?date_from=${dateFrom}&date_to=${dateTo}`);
      if (r.ok) {
        const data = await r.json();
        setRecords(Array.isArray(data) ? data : []);
        if (!Array.isArray(data)) {
          setError(`Unexpected response format: ${JSON.stringify(data).slice(0, 120)}`);
        }
      } else {
        const body = await r.json().catch(() => ({}));
        setError(`Error ${r.status}: ${body.error || body.message || r.statusText}`);
      }
    } catch (e: any) {
      setError(`Could not reach the agent backend: ${e.message}`);
    }
    setLoading(false);
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    agentFetch('/admin/api/orgs')
      .then(r => r.ok ? r.json() : [])
      .then(data => setOrgs(data))
      .catch(() => {});
  }, [isSuperAdmin]);

  // Derive unique users from records for pills
  const uniqueUsers = Array.from(
    new Map(records.filter(r => r.user_id).map(r => [r.user_id, { id: r.user_id, name: r.user_name, email: r.user_email }])).values()
  ).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const orgMap = Object.fromEntries(orgs.map(o => [o.id, o.name]));

  // Change 1: pills only for firms that actually have usage in the loaded (date-filtered) records.
  const usedOrgIds = new Set(records.map(r => r.org_id).filter(Boolean));
  const pillOrgs = ONLY_SHOW_USED_FIRM_PILLS ? orgs.filter(o => usedOrgIds.has(o.id)) : orgs;

  // Only super_admin ever sees a £ cost figure. org_admin sees the real
  // (un-multiplied) token count instead — no client-side markup on tokens.
  const realTokens = (r: any) => (r.prompt_tokens || 0) + (r.output_tokens || 0);
  const minCostNum = parseFloat(minCost);
  const filtered = records
    .filter(r => !selectedOrgId  || r.org_id   === selectedOrgId)
    .filter(r => !selectedUserId || r.user_id  === selectedUserId)
    .filter(r => isNaN(minCostNum) || (isSuperAdmin ? (r.cost_gbp || 0) : realTokens(r)) >= minCostNum);

  const totalCost = filtered.reduce((s, r) => s + (r.cost_gbp || 0), 0);
  const totalTokens = filtered.reduce((s, r) => s + realTokens(r), 0);

  return (
    <div>
      {/* Org pills — super admin only */}
      {isSuperAdmin && pillOrgs.length > 0 && (
        <div className="ai-org-pills">
          <button className={`ai-org-pill${!selectedOrgId ? ' active' : ''}`} onClick={() => setSelectedOrgId(null)}>All Orgs</button>
          {pillOrgs.map(o => (
            <button
              key={o.id}
              className={`ai-org-pill${selectedOrgId === o.id ? ' active' : ''}`}
              onClick={() => setSelectedOrgId(o.id === selectedOrgId ? null : o.id)}
            >{o.name}</button>
          ))}
        </div>
      )}

      {/* User pills */}
      {uniqueUsers.length > 0 && (
        <div className="ai-org-pills" style={{ marginTop: 8 }}>
          <button className={`ai-org-pill${!selectedUserId ? ' active' : ''}`} onClick={() => setSelectedUserId(null)}>All Users</button>
          {uniqueUsers.map(u => (
            <button
              key={u.id}
              className={`ai-org-pill${selectedUserId === u.id ? ' active' : ''}`}
              onClick={() => setSelectedUserId(u.id === selectedUserId ? null : u.id)}
              title={u.email || ''}
            >{u.name || u.email || u.id}</button>
          ))}
        </div>
      )}

      {/* Filters row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, marginTop: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label className="form-label" style={{ fontSize: 12 }}>From</label>
          <input type="date" className="form-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        </div>
        <div>
          <label className="form-label" style={{ fontSize: 12 }}>To</label>
          <input type="date" className="form-input" value={dateTo} onChange={e => setDateTo(e.target.value)} />
        </div>
        <div>
          <label className="form-label" style={{ fontSize: 12 }}>{isSuperAdmin ? 'Min cost (£)' : 'Min tokens'}</label>
          <input
            type="number"
            min="0"
            step={isSuperAdmin ? '0.0001' : '1'}
            placeholder={isSuperAdmin ? 'e.g. 0.01' : 'e.g. 100'}
            className="form-input"
            style={{ width: 110 }}
            value={minCost}
            onChange={e => setMinCost(e.target.value)}
          />
        </div>
        <button className="btn btn-sm btn-outline" onClick={load}>Refresh</button>
      </div>

      {loading && <p className="text-muted text-sm">Loading…</p>}
      {!loading && error && (
        <p style={{ color: 'var(--c-error)', fontSize: 13, marginBottom: 8 }}>⚠ {error}</p>
      )}
      {!loading && !error && (
        <>
          <p className="text-sm" style={{ marginBottom: 8 }}>
            <strong>{filtered.length}</strong> records · {isSuperAdmin
              ? <>Total: <strong>£{totalCost.toFixed(4)}</strong></>
              : <>Total tokens: <strong>{totalTokens.toLocaleString()}</strong></>}
          </p>
          {filtered.length === 0 ? (
            <p className="text-muted text-sm">No usage records found for this period.</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date &amp; Time</th>
                    {isSuperAdmin && <th>Org</th>}
                    <th>User</th>
                    <th>Model</th>
                    {isSuperAdmin ? (
                      <>
                        <th>Input</th>
                        <th>Output</th>
                        <th>Cost</th>
                      </>
                    ) : (
                      <th>Tokens</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 100).map((r, i) => {
                    const ts = r.timestamp ? new Date(r.timestamp) : null;
                    const dateTime = ts
                      ? `${ts.toLocaleDateString()} ${ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                      : r.date;
                    const hasQuery = ENABLE_QUERY_HOVER && !!r.query;
                    return (
                      <tr
                        key={i}
                        style={hasQuery ? { cursor: ENABLE_QUERY_DETAIL ? 'pointer' : 'help' } : undefined}
                        onClick={hasQuery && ENABLE_QUERY_DETAIL ? () => { setHoverQuery(null); setDetail(r); } : undefined}
                        onMouseEnter={hasQuery ? (e => setHoverQuery({ query: r.query, answer: r.answer || '', x: e.clientX, y: e.clientY })) : undefined}
                        onMouseMove={hasQuery ? (e => setHoverQuery(q => (q ? { ...q, x: e.clientX, y: e.clientY } : q))) : undefined}
                        onMouseLeave={hasQuery ? () => setHoverQuery(null) : undefined}
                      >
                        <td style={{ whiteSpace: 'nowrap' }}>{dateTime}</td>
                        {isSuperAdmin && <td>{orgMap[r.org_id] || r.org_id}</td>}
                        <td>
                          <div style={{ fontSize: 12 }}>{r.user_name || '—'}</div>
                          <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>{r.user_email || ''}</div>
                        </td>
                        <td>{r.model}</td>
                        {isSuperAdmin ? (
                          <>
                            <td>{(r.prompt_tokens || 0).toLocaleString()}</td>
                            <td>{(r.output_tokens || 0).toLocaleString()}</td>
                            <td>£{(r.cost_gbp || 0).toFixed(4)}</td>
                          </>
                        ) : (
                          <td>{realTokens(r).toLocaleString()}</td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {ENABLE_QUERY_HOVER && hoverQuery && (
        <div style={{
          position: 'fixed',
          left: Math.min(hoverQuery.x + 14, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 400),
          top: hoverQuery.y + 14,
          maxWidth: 380,
          maxHeight: 420,
          overflow: 'hidden',
          background: '#1f2937',
          color: '#fff',
          padding: ENABLE_QUERY_DETAIL ? '10px 12px 30px' : '10px 12px',
          borderRadius: 6,
          fontSize: 12,
          lineHeight: 1.45,
          zIndex: 1000,
          boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
          pointerEvents: 'none',
          whiteSpace: 'pre-wrap',
        }}>
          <div style={{ fontWeight: 700, opacity: 0.65, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 2 }}>Question</div>
          <div style={{ marginBottom: hoverQuery.answer ? 8 : 0 }}>{hoverQuery.query}</div>
          {hoverQuery.answer && (
            <>
              <div style={{ fontWeight: 700, opacity: 0.65, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 2 }}>Answer</div>
              <div>{hoverQuery.answer}</div>
            </>
          )}
          {ENABLE_QUERY_DETAIL && (
            <div style={{
              position: 'absolute', left: 0, right: 0, bottom: 0,
              padding: '6px 12px', background: '#111827', fontSize: 10, opacity: 0.85,
            }}>Click row for full text &amp; copy</div>
          )}
        </div>
      )}

      {ENABLE_QUERY_DETAIL && detail && (() => {
        const ts = detail.timestamp ? new Date(detail.timestamp) : null;
        const when = ts ? `${ts.toLocaleDateString()} ${ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : detail.date;
        const label: React.CSSProperties = { fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--c-text-muted)' };
        const box: React.CSSProperties = {
          margin: 0, padding: '10px 12px', borderRadius: 6, border: '1px solid var(--c-border)',
          background: 'var(--c-bg)', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          fontFamily: 'inherit', fontSize: 13, lineHeight: 1.5, userSelect: 'text', overflowY: 'auto',
        };
        const copyBtn = (text: string, which: 'q' | 'a') => (
          <button className="btn btn-sm" onClick={() => copyText(text, which)} style={{ fontSize: 12 }}>
            {copied === which ? 'Copied ✓' : 'Copy'}
          </button>
        );
        return (
          <div
            onClick={() => setDetail(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          >
            <div
              onClick={e => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              style={{
                width: 'min(800px, 92vw)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', gap: 10,
                background: 'var(--c-surface, #fff)', color: 'var(--c-text, #111)', borderRadius: 10, padding: 18,
                boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, fontSize: 12, color: 'var(--c-text-muted)' }}>
                  {[when, detail.user_email || detail.user_name, detail.model].filter(Boolean).join(' · ')}
                </div>
                <button onClick={() => setDetail(null)} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'inherit', lineHeight: 1 }}>✕</button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={label}>Question</span>
                {copyBtn(detail.query || '', 'q')}
              </div>
              <pre style={{ ...box, maxHeight: '25vh', flexShrink: 0 }}>{detail.query}</pre>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={label}>Answer</span>
                {detail.answer ? copyBtn(detail.answer, 'a') : null}
              </div>
              <pre style={{ ...box, flex: 1, minHeight: 80 }}>{detail.answer || '(no answer recorded)'}</pre>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

interface IndexedDoc {
  file_id: string;
  name: string;
  category: string;
  date: string;
}

interface UnindexedDoc {
  file_id: string;
  name: string;
  category: string;
  mimeType: string;
}

// ----- Sync Tab -----
function SyncTab() {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({ status: 'idle' });
  const [polling, setPolling] = useState(false);
  const [docCount, setDocCount] = useState<number | null>(null);
  const [indexedDocs, setIndexedDocs] = useState<IndexedDoc[]>([]);
  const [unindexedDocs, setUnindexedDocs] = useState<UnindexedDoc[]>([]);
  const [loadingUnindexed, setLoadingUnindexed] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const pollingRef = useRef(false);

  const loadDocCount = useCallback(async () => {
    try {
      const r = await agentFetch('/api/sync/indexed-documents');
      if (r.ok) {
        const d = await r.json();
        setDocCount(d.total_chunks ?? d.documents?.length ?? null);
        setIndexedDocs(d.documents ?? []);
      }
    } catch (_) {}
  }, []);

  const loadUnindexed = useCallback(async () => {
    setLoadingUnindexed(true);
    try {
      const r = await agentFetch('/api/sync/unindexed');
      if (r.ok) {
        const d = await r.json();
        setUnindexedDocs(d.unindexed ?? []);
      }
    } catch (_) {}
    setLoadingUnindexed(false);
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const r = await agentFetch('/api/sync/job-status');
      if (r.ok) {
        const d = await r.json();
        const wasRunning = pollingRef.current;
        setSyncStatus(d);
        if (d.status === 'running') {
          pollingRef.current = true;
          setPolling(true);
        } else {
          pollingRef.current = false;
          setPolling(false);
          if (wasRunning) {
            loadDocCount();
            loadUnindexed();
          }
        }
      }
    } catch (_) {}
  }, [loadDocCount, loadUnindexed]);

  useEffect(() => {
    loadStatus();
    loadDocCount();
    loadUnindexed();
  }, [loadStatus, loadDocCount, loadUnindexed]);

  useEffect(() => {
    if (!polling) return;
    const id = setInterval(loadStatus, 3000);
    return () => clearInterval(id);
  }, [polling, loadStatus]);

  const triggerSync = async () => {
    try {
      await agentFetch('/api/sync', { method: 'POST' });
      setSyncStatus({ status: 'running' });
      setPolling(true);
    } catch (_) {}
  };

  const triggerSelectiveSync = async () => {
    const fileIds = unindexedDocs.map(d => d.file_id);
    try {
      await agentFetch('/api/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_ids: fileIds }),
      });
      setSyncStatus({ status: 'running' });
      setPolling(true);
    } catch (_) {}
  };

  const resetSync = async () => {
    await agentFetch('/api/sync/reset', { method: 'POST' });
    loadStatus();
  };

  const toggleDoc = (fileId: string) => {
    setSelectedDocs(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId); else next.add(fileId);
      return next;
    });
  };

  const toggleAllDocs = () => {
    setSelectedDocs(prev =>
      prev.size === indexedDocs.length ? new Set() : new Set(indexedDocs.map(d => d.file_id)),
    );
  };

  const deleteSelected = async () => {
    const fileIds = [...selectedDocs];
    if (fileIds.length === 0) return;
    const names = indexedDocs.filter(d => selectedDocs.has(d.file_id)).map(d => d.name);
    const preview = names.slice(0, 10).join('\n');
    const more = names.length > 10 ? `\n…and ${names.length - 10} more` : '';
    if (!window.confirm(
      `Remove ${fileIds.length} document(s) from the index?\n\n${preview}${more}\n\nA full sync will re-index any that are still in the Drive library.`,
    )) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const r = await agentFetch('/api/sync/documents/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_ids: fileIds }),
      });
      if (r.ok) {
        setSelectedDocs(new Set());
        await loadDocCount();
        await loadUnindexed();
      } else {
        const d = await r.json().catch(() => ({}));
        setDeleteError(d.error || `Delete failed (${r.status}).`);
      }
    } catch (e: any) {
      setDeleteError(e?.message || 'Delete failed.');
    }
    setDeleting(false);
  };

  return (
    <div>
      <div className="admin-stats" style={{ marginBottom: 20 }}>
        <div className="stat-card">
          <div className="stat-value">{docCount ?? '—'}</div>
          <div className="stat-label">Indexed chunks</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ textTransform: 'capitalize' }}>{syncStatus.status}</div>
          <div className="stat-label">Sync status</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: unindexedDocs.length > 0 ? '#e67e22' : undefined }}>
            {loadingUnindexed ? '…' : unindexedDocs.length}
          </div>
          <div className="stat-label">Unindexed docs</div>
        </div>
      </div>

      {syncStatus.status === 'running' && (
        <p className="text-sm" style={{ marginBottom: 12 }}>
          Syncing… {syncStatus.doc_name && `(${syncStatus.doc_name})`}
          {syncStatus.doc_total ? ` ${syncStatus.doc_num ?? 0}/${syncStatus.doc_total}` : ''}
        </p>
      )}
      {syncStatus.error && (
        <div className="alert alert-error" style={{ marginBottom: 12 }}>{syncStatus.error}</div>
      )}
      {syncStatus.status === 'done' &&
        ((syncStatus.docs_added?.length ?? 0) > 0 ||
         (syncStatus.docs_removed?.length ?? 0) > 0 ||
         (syncStatus.docs_updated?.length ?? 0) > 0) && (
        <p className="text-sm" style={{ marginBottom: 12 }}>
          Last sync — added: {syncStatus.docs_added?.length ?? 0},{' '}
          removed: {syncStatus.docs_removed?.length ?? 0},{' '}
          moved/renamed: {syncStatus.docs_updated?.length ?? 0}
          {(syncStatus.docs_updated?.length ?? 0) > 0 &&
            ` (${syncStatus.docs_updated!.join(', ')})`}
        </p>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 24 }}>
        <button
          className="btn btn-primary"
          onClick={triggerSync}
          disabled={syncStatus.status === 'running'}
        >
          {syncStatus.status === 'running' ? 'Syncing…' : 'Trigger Full Sync'}
        </button>
        <button
          className="btn btn-primary"
          onClick={triggerSelectiveSync}
          disabled={syncStatus.status === 'running' || unindexedDocs.length === 0}
          style={{ background: '#e67e22', borderColor: '#e67e22' }}
        >
          Sync Missing ({unindexedDocs.length})
        </button>
        <button className="btn btn-outline" onClick={resetSync}>
          Reset stuck sync
        </button>
      </div>

      {unindexedDocs.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <p className="text-sm" style={{ marginBottom: 8, fontWeight: 600, color: '#e67e22' }}>
            Unindexed Documents ({unindexedDocs.length})
          </p>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr><th>Document</th><th>Category</th><th>Type</th></tr>
              </thead>
              <tbody>
                {unindexedDocs.map((doc, i) => (
                  <tr key={i}>
                    <td>{doc.name}</td>
                    <td>{doc.category}</td>
                    <td>{doc.mimeType.split('/').pop()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {indexedDocs.length > 0 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <p className="text-sm" style={{ margin: 0, fontWeight: 600 }}>
              Currently Indexed Documents ({indexedDocs.length})
            </p>
            <button
              className="btn btn-outline"
              onClick={deleteSelected}
              disabled={selectedDocs.size === 0 || deleting || syncStatus.status === 'running'}
              style={{ color: '#c0392b', borderColor: '#c0392b' }}
            >
              {deleting ? 'Deleting…' : `Delete Selected (${selectedDocs.size})`}
            </button>
          </div>
          {deleteError && (
            <div className="alert alert-error" style={{ marginBottom: 8 }}>{deleteError}</div>
          )}
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <input
                      type="checkbox"
                      checked={selectedDocs.size === indexedDocs.length && indexedDocs.length > 0}
                      onChange={toggleAllDocs}
                      title="Select all"
                    />
                  </th>
                  <th>Document</th><th>Category</th><th>Date</th>
                </tr>
              </thead>
              <tbody>
                {indexedDocs.map((doc) => (
                  <tr key={doc.file_id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedDocs.has(doc.file_id)}
                        onChange={() => toggleDoc(doc.file_id)}
                      />
                    </td>
                    <td>{doc.name}</td>
                    <td>{doc.category}</td>
                    <td>{doc.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ----- Main Panel -----
export const AIAdminPanel: React.FC<Props> = ({ isSuperAdmin }) => {
  const [activeTab, setActiveTab] = useState<AdminTab>('usage');

  // Non-super-admins only see Usage & Costs — no tab bar needed
  if (!isSuperAdmin) {
    return (
      <div className="ai-admin-panel">
        <h3 style={{ marginBottom: 16 }}>Usage &amp; Costs</h3>
        <UsageTab isSuperAdmin={false} />
      </div>
    );
  }

  const tabs: { id: AdminTab; label: string }[] = [
    { id: 'orgs',  label: 'Organisations' },
    { id: 'usage', label: 'Usage & Costs' },
    { id: 'sync',  label: 'Sync' },
  ];

  return (
    <div className="ai-admin-panel">
      <h3 style={{ marginBottom: 16 }}>Agent Admin</h3>
      <div className="filter-bar" style={{ marginBottom: 20 }}>
        {tabs.map(t => (
          <button
            key={t.id}
            className={`filter-btn${activeTab === t.id ? ' active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'orgs'  && <OrgsTab />}
      {activeTab === 'usage' && <UsageTab isSuperAdmin={true} />}
      {activeTab === 'sync'  && <SyncTab />}
    </div>
  );
};
